<template>
  <section class="table-items table-items-quotes" v-loading="loading">
    <div class="summary-cards mb-4">
      <div class="summary-card">
        <span class="summary-card__label">Ventas brutas</span>
        <span class="summary-card__value">
          <FormatAmount :amount="products.total_sales" abbreviate />
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-card__label">Notas de crédito</span>
        <span class="summary-card__value">
          <FormatAmount :amount="products.total_credit_notes" abbreviate />
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-card__label">Ventas total</span>
        <span class="summary-card__value">
          <FormatAmount :amount="products.total_net_sales" abbreviate />
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-card__label">Productos vendidos</span>
        <span class="summary-card__value">{{
          products.total_quantity_products || 0
        }}</span>
      </div>
    </div>

    <div class="dir-toolbar">
      <div class="dir-toolbar-search">
        <InputSearchComponent @onChangeSearch="onChangeSearch" />
      </div>
      <FilterBetweenDatesSelect
        :defaultMonthValue="true"
        @onAsyncDataForDates="onAsyncDataForDates"
      />
      <el-checkbox
        v-if="hasAlternativeCodeProducts"
        v-model="filterByAlternativeCode"
        class="spl-alt-check"
      >
        Solo código alternativo
      </el-checkbox>
      <div class="dir-toolbar-right">
        <ExportButtons
          v-if="resultQueryProducts.length"
          :data="exportData"
          :columns="exportColumns"
          filename="ventas-productos"
        />
      </div>
    </div>

    <div v-if="!loading && resultQueryProducts.length === 0" class="spl-empty">
      <i class="el-icon-data-line"></i>
      <p>No hay datos de ventas para el período seleccionado</p>
    </div>

    <template v-if="resultQueryProducts.length">
      <b-row class="table-items-header">
        <b-col xs="12" sm="12" md="2">
          <h6 class="h6 ml-2">Código</h6>
        </b-col>
        <b-col xs="12" sm="12" md="3">
          <h6 class="h6">Detalle</h6>
        </b-col>
        <b-col xs="6" sm="12" md="3">
          <h6 class="h6">Categoria</h6>
        </b-col>
        <b-col xs="6" sm="12" md="1">
          <h6 class="h6 spl-sortable" @click="toggleSort('quantity')">
            Vendido
            <i :class="sortIcon('quantity')"></i>
          </h6>
        </b-col>
        <b-col xs="4" sm="12" md="1">
          <h6 class="h6 spl-sortable" @click="toggleSort('sales')">
            Sub total
            <i :class="sortIcon('sales')"></i>
          </h6>
        </b-col>
        <b-col xs="4" sm="12" md="1">
          <h6 class="h6">Nota crédito</h6>
        </b-col>
        <b-col xs="4" sm="12" md="1">
          <h6 class="h6 spl-sortable" @click="toggleSort('net_sales')">
            Venta total
            <i :class="sortIcon('net_sales')"></i>
          </h6>
        </b-col>
      </b-row>
      <paginate
        class="wrapper-pagination"
        name="resultQueryProducts"
        :list="resultQueryProducts"
        :per="perPaginate"
        :key="searchQuery"
      >
        <div
          class="card table-items-body"
          :class="{ 'cursor-pointer': !item.code_breakdown }"
          v-for="(item, index) in paginated('resultQueryProducts')"
          :key="index"
          @click="!item.code_breakdown && openProductDetail(item)"
        >
          <!-- Main product row -->
          <div
            class="card-body table-items-content"
            :class="{ 'spl-has-breakdown': item.code_breakdown }"
            @click="item.code_breakdown && openProductDetail(item)"
          >
            <b-row class="">
              <b-col xs="12" sm="12" md="2" class="table-col-item">
                <div class="spl-codes">
                  <span class="item-col-code font-semi-bold">{{
                    item.code
                  }}</span>
                  <span v-if="item.alternative_code" class="spl-alt-code">{{
                    item.alternative_code
                  }}</span>
                </div>
              </b-col>
              <b-col xs="12" sm="12" md="3" class="table-col-item">
                <span
                  class="item-col-detail font-bold text-primary str-limit-space"
                  >{{ item.name }}</span
                >
              </b-col>
              <b-col xs="6" sm="12" md="3" class="table-col-item">
                <span class="font-semi-bold" v-if="item.category">{{
                  item.category.title
                }}</span>
              </b-col>
              <b-col xs="6" sm="12" md="1" class="table-col-item">
                <span class="font-semi-bold"> {{ item.quantity }}</span>
              </b-col>
              <b-col xs="4" sm="12" md="1" class="table-col-item">
                <span class="font-semi-bold"
                  ><FormatAmount :amount="item.sales"
                /></span>
              </b-col>
              <b-col xs="4" sm="12" md="1" class="table-col-item">
                <span class="font-semi-bold"
                  ><FormatAmount :amount="item.credit_note_sales"
                /></span>
              </b-col>
              <b-col xs="4" sm="12" md="1" class="table-col-item">
                <span class="font-semi-bold"
                  ><FormatAmount :amount="item.net_sales"
                /></span>
              </b-col>
            </b-row>
          </div>

          <!-- Code breakdown sub-rows -->
          <template v-if="item.code_breakdown">
            <div
              v-for="(row, type) in item.code_breakdown"
              :key="type"
              class="spl-breakdown-row"
              :class="'spl-breakdown-row--' + type"
              @click.stop="openProductDetail(item)"
            >
              <b-row>
                <b-col xs="12" sm="12" md="2" class="table-col-item">
                  <span
                    class="spl-breakdown-badge"
                    :class="'spl-breakdown-badge--' + type"
                  >
                    {{ type === "primary" ? "Normal" : "Alternativo" }}
                  </span>
                  <span class="spl-breakdown-code">{{ row.code }}</span>
                </b-col>
                <b-col xs="12" sm="12" md="6" class="table-col-item"></b-col>
                <b-col xs="6" sm="12" md="1" class="table-col-item">
                  <span class="spl-breakdown-val">{{ row.quantity }}</span>
                </b-col>
                <b-col xs="4" sm="12" md="1" class="table-col-item">
                  <span class="spl-breakdown-val"
                    ><FormatAmount :amount="row.sales"
                  /></span>
                </b-col>
                <b-col xs="4" sm="12" md="1" class="table-col-item">
                  <span class="spl-breakdown-val"
                    ><FormatAmount :amount="row.credit_note_sales"
                  /></span>
                </b-col>
                <b-col xs="4" sm="12" md="1" class="table-col-item">
                  <span class="spl-breakdown-val font-semi-bold"
                    ><FormatAmount :amount="row.net_sales"
                  /></span>
                </b-col>
              </b-row>
            </div>
          </template>
        </div>
      </paginate>

      <paginate-links
        v-show="resultQueryProducts.length > perPaginate"
        for="resultQueryProducts"
        :classes="{ ul: 'pagination', li: 'page-item', a: 'page-link' }"
        :simple="{
          next: 'Siguiente »',
          prev: '« Regresar'
        }"
      ></paginate-links>
    </template>

    <ProductInvoicesModal
      v-if="selectedProduct"
      :product="selectedProduct"
      :search-dates="searchDates || {}"
      @close="selectedProduct = null"
    />
  </section>
</template>

<script>
import reportService from "../services/reportService";
import InputSearchComponent from "../../components/InputSearch";
import FilterBetweenDatesSelect from "../../components/FilterBetweenDatesSelect";
import FormatAmount from "@/components/General/FormatAmount";
import ProductInvoicesModal from "./ProductInvoicesModal";

export default {
  name: "SalesProductList",
  data() {
    return {
      paginate: ["resultQueryProducts"],
      perPaginate: 12,
      products: [],
      searchQuery: null,
      filterByAlternativeCode: false,
      loading: false,
      selectedProduct: null,
      sortField: "net_sales",
      sortOrder: "desc"
    };
  },
  computed: {
    exportColumns() {
      const cols = [
        { label: "Código", field: "code" },
        { label: "Nombre", field: "name" },
        { label: "Categoría", field: "category_title" },
        { label: "Vendido", field: "quantity" },
        { label: "Sub total", field: "sales" },
        { label: "Nota crédito", field: "credit_note_sales" },
        { label: "Venta total", field: "net_sales" }
      ];
      if (this.hasAlternativeCodeProducts) {
        cols.splice(1, 0, {
          label: "Código alternativo",
          field: "alternative_code"
        });
      }
      return cols;
    },
    exportData() {
      return this.resultQueryProducts.map(item => ({
        ...item,
        category_title: item.category ? item.category.title : ""
      }));
    },
    hasAlternativeCodeProducts() {
      const { products } = this.products;
      return products && products.some(item => item.alternative_code);
    },
    resultQueryProducts() {
      const { products } = this.products;
      const filtered =
        (products &&
          products.concat().filter(item => {
            if (this.filterByAlternativeCode && !item.alternative_code) {
              return false;
            }
            if (!this.searchQuery) return true;
            const query = this.searchQuery.toLowerCase();
            return (
              item.name.toLowerCase().includes(query) ||
              item.code.toLowerCase().includes(query) ||
              (item.alternative_code &&
                item.alternative_code.toLowerCase().includes(query))
            );
          })) ||
        [];

      if (!this.sortField) return filtered;

      return filtered.sort((a, b) => {
        const valA = parseFloat(a[this.sortField]) || 0;
        const valB = parseFloat(b[this.sortField]) || 0;
        return this.sortOrder === "asc" ? valA - valB : valB - valA;
      });
    }
  },
  methods: {
    toggleSort(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === "desc" ? "asc" : "desc";
      } else {
        this.sortField = field;
        this.sortOrder = "desc";
      }
    },
    sortIcon(field) {
      if (this.sortField !== field) return "el-icon-sort spl-sort-neutral";
      return this.sortOrder === "desc"
        ? "el-icon-sort-down spl-sort-active"
        : "el-icon-sort-up spl-sort-active";
    },
    onChangeSearch({ search }) {
      this.searchQuery = search;
    },
    onAsyncDataForDates({ searchDates }) {
      this.searchDates = searchDates;
      this.products = [];
      this.getSalesProducts();
    },
    async downloadFile() {
      this.loading = true;

      try {
        const response = await reportService
          .getDownloadReportExcel({
            params: {
              ...{},
              ...this.searchDates
            },
            headers: {
              responseType: "blob",
              ContentType: "application/vnd.ms-excel"
            }
          })
          .finally(() => {
            this.loading = false;
          });

        const { data, success } = response.data;
        if (success) {
          const date = new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/-/g, "_")
            .replace("T", "_");
          const anchor = document.createElement("a");
          const filename = `${date}_reporte-ventas.xlsx`;
          anchor.setAttribute("download", filename);
          anchor.setAttribute("href", data);
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          this.$notifications.success({
            message: "Excel descargado exitosamente"
          });
        }
      } catch (error) {
        return false;
      }

      /* const response = await reportService
        .getDownloadReportExcel({
          params: {
            ...{},
            ...{ excel: true },
            ...this.searchDates
          },
          headers: {
            responseType: 'blob',
            ContentType: 'application/vnd.ms-excel'
          }
        })
        .then(res => {
          const anchor = document.createElement("a");
          const filename = this.date+"payroll.xlsx";
          anchor.setAttribute("download", filename);
          anchor.setAttribute("href", res.data.data);
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }); */
    },
    openProductDetail(item) {
      this.selectedProduct = item;
    },
    async getSalesProducts() {
      try {
        this.loading = true;
        const response = await reportService.getSalesProducts({
          params: {
            ...this.searchDates,
            _report: "products"
          }
        });

        if (response && response.data && response.data.success) {
          this.products = response.data.data;
        }
      } catch (error) {
        /* silent */
      } finally {
        this.loading = false;
      }
    }
  },
  components: {
    InputSearchComponent,
    FilterBetweenDatesSelect,
    FormatAmount,
    ProductInvoicesModal
  },
  mounted() {
    // Initial data load handled by FilterBetweenDatesSelect -> onAsyncDataForDates
  }
};
</script>

<style lang="scss" scoped>
.btn-sm {
  font-size: 12px;
  line-height: 1.5;
  padding: 5px 10px;
}
.cursor-pointer {
  cursor: pointer;
}
.cursor-pointer:hover {
  background-color: #f5f7fa;
}
/* ── Toolbar — uses global .dir-toolbar from main.scss ── */
.spl-alt-check {
  white-space: nowrap;
}

.spl-empty {
  text-align: center;
  padding: 60px 20px;
  color: #aaa;

  i {
    font-size: 40px;
    color: #c0c4d0;
    display: block;
    margin-bottom: 12px;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
}

.spl-sortable {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
  &:hover {
    color: #3ecfb4;
  }
  i {
    font-size: 13px;
  }
}

.spl-sort-neutral {
  color: #c0c4cc;
}

.spl-sort-active {
  color: #3ecfb4;
}

/* ── Dual-code column ── */
.spl-codes {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.spl-alt-code {
  font-size: 11px;
  color: #909399;
  font-weight: 500;
}

/* ── Breakdown sub-rows ── */
.spl-has-breakdown {
  cursor: pointer;
  border-bottom: 1px solid #f0f2f8;
}

.spl-breakdown-row {
  padding: 6px 20px;
  cursor: pointer;
  transition: background 0.1s;

  &:hover {
    background: #f7f8fc;
  }

  &--primary {
    background: #fafbff;
    border-bottom: 1px solid #f0f2f8;
  }

  &--alternative {
    background: #f7fdf9;
  }

  .table-col-item {
    padding-top: 4px;
    padding-bottom: 4px;
  }
}

.spl-breakdown-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
  margin-right: 6px;
  vertical-align: middle;

  &--primary {
    background: #eef0f8;
    color: #2d2b55;
  }

  &--alternative {
    background: #e1f5ee;
    color: #1d9e75;
  }
}

.spl-breakdown-code {
  font-size: 12px;
  color: #606266;
  font-weight: 500;
  vertical-align: middle;
}

.spl-breakdown-val {
  font-size: 13px;
  color: #606266;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.summary-card {
  background: #fff;
  border: 1.5px solid #eef0f8;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__label {
    font-size: 11px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    color: #2d2b55;
  }
}
</style>
